import { Directive, ElementRef, inject, Input } from '@angular/core';

import { Constructor } from '@kros-sk/utils-fn/object';
import { DeviceDetectorServiceOld, DeviceType } from '@kros-sk/device-detector';
import { getScrollableParent } from '@kros-sk/shared/utils-fn/scroll-utils';

import { KrosFormFieldComponent } from '../kros-form-field/kros-form-field.component';

export interface CanScroll {
  scrollToViewportTop(): void;
}

type CanScrollCtor = Constructor<CanScroll>;

export function Scrollable<TBase extends Constructor<object>>(base: TBase): CanScrollCtor & TBase {

  @Directive()
  class Scrollable extends base {
    @Input() stopScrollInModal = false;

    private deviceDetector = inject(DeviceDetectorServiceOld);
    private inputEl = inject(ElementRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>);
    private containerEl = inject(KrosFormFieldComponent, { optional: true })?.elementRef;

    scrollToViewportTop(): void {
      if (!this.stopScrollInModal && (this.deviceDetector.deviceType === DeviceType.MOBILE || this.isOverlapped())) {
        this.ensureInView();
      }
    }

    private isOverlapped(): boolean {
      const inputRect = this.inputEl.nativeElement.getBoundingClientRect();
      const margin = inputRect.height * 0.3;
      const centerX = inputRect.left + inputRect.width / 2;

      const elTop = this.getElementFromPoint(centerX, inputRect.top + margin);
      if (!elTop) {
        return false;
      }
      const elBottom = this.getElementFromPoint(centerX, inputRect.bottom - margin);
      if (!elBottom) {
        return false;
      }

      return !elTop.contains(this.inputEl.nativeElement) || !elBottom.contains(this.inputEl.nativeElement);
    }

    /**
     * this method hides from elementFromPoint cdk overlay related elements to not have false positive result
     */
    private getElementFromPoint(x: number, y: number): Element | null {
      const overlays = document.querySelectorAll<HTMLElement>('[class*="cdk-overlay"]');
      const origStyles = new Map<HTMLElement, string>();

      overlays.forEach((el) => {
        origStyles.set(el, el.style.pointerEvents);
        el.style.pointerEvents = 'none';
      });

      const element = document.elementFromPoint(x, y);

      overlays.forEach((el) => {
        const style = origStyles.get(el);
        if (style !== undefined) {
          el.style.pointerEvents = style;
        }
      });

      return element;
    }

    private ensureInView(): void {
      const isModalOpened = document.body.classList.contains('modal-opened');
      if (isModalOpened) {
        document.body.classList.remove('modal-opened');
      }

      const container = this.containerEl?.nativeElement;
      const scrollableParent = container && getScrollableParent(container);

      if (scrollableParent) {
        scrollableParent.element.scrollTop = 0;
        const elementBounding = container.getBoundingClientRect();
        const parentBounding = scrollableParent.element.getBoundingClientRect();
        const elementTopRelative = elementBounding.top - parentBounding.top;

        scrollableParent.element.scrollTop = elementTopRelative - scrollableParent.unscrollableTop;
      } else {
        this.inputEl.nativeElement.scrollIntoView();
      }

      if (isModalOpened) {
        document.body.classList.add('modal-opened');
      }
    }
  }

  return Scrollable;
}
